import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Ship } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Vessel = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  // ── State ──────────────────────────────────────────────────────────────────
  const [vessels, setVessels]               = useState([]);
  const [filteredVessels, setFilteredVessels] = useState([]);
  const [carriers, setCarriers]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [activeOnly, setActiveOnly]         = useState(false);
  const [showModal, setShowModal]           = useState(false);
  const [editMode, setEditMode]             = useState(false);
  const [submitLoading, setSubmitLoading]   = useState(false);

  const emptyForm = {
    VesselID:  null,
    Vessel:    '',
    CarrierID: null,
    IsActive:  true
  };

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors]     = useState({});

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchCarriers();
    fetchVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  // Re-fetch when activeOnly filter changes
  useEffect(() => {
    if (companyId && yearId) fetchVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  // Client-side search
  useEffect(() => {
    filterVessels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, vessels]);

  // ── API ────────────────────────────────────────────────────────────────────
  const fetchCarriers = async () => {
    try {
      const res = await apiClient.get('/Vessel.php?action=carriers');
      if (res.data.success) setCarriers(res.data.data || []);
    } catch (err) {
      console.error('Error fetching carriers:', err);
    }
  };

  const fetchVessels = async () => {
    setLoading(true);
    try {
      const params = activeOnly ? { active: '1' } : {};
      const res    = await apiClient.get('/Vessel.php', { params });
      if (res.data.success) {
        setVessels(res.data.data || []);
      } else {
        alert('Failed to fetch vessels: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error fetching vessels:', err);
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching vessels.');
    } finally {
      setLoading(false);
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filterVessels = () => {
    if (!searchTerm.trim()) { setFilteredVessels(vessels); return; }
    const q = searchTerm.toLowerCase();
    setFilteredVessels(
      vessels.filter(v =>
        v.Vessel.toLowerCase().includes(q) ||
        v.CarrierName?.toLowerCase().includes(q)
      )
    );
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.Vessel.trim()) errs.Vessel = 'Vessel name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        IsActive:  formData.IsActive  ? 1 : 0,
        CarrierID: formData.CarrierID ? parseInt(formData.CarrierID) : null
      };

      const method = editMode ? 'put' : 'post';
      const res    = await apiClient[method]('/Vessel.php', payload);

      if (res.data.success) {
        alert(editMode ? 'Vessel updated successfully!' : 'Vessel created successfully!');
        setShowModal(false);
        resetForm();
        fetchVessels();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error saving vessel:', err);
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving vessel: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = async (vessel) => {
    try {
      const res = await apiClient.get(`/Vessel.php?id=${vessel.VesselID}`);
      if (res.data.success) {
        const d = res.data.data;
        setFormData({
          VesselID:  d.VesselID,
          Vessel:    d.Vessel    || '',
          CarrierID: d.CarrierID || null,
          IsActive:  Number(d.IsActive) === 1,
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (err) {
      console.error('Error loading vessel:', err);
      alert('Error loading vessel details');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vessel?')) return;
    try {
      const res = await apiClient.delete('/Vessel.php', { data: { VesselID: id } });
      if (res.data.success) {
        alert('Vessel deleted successfully!');
        fetchVessels();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error('Error deleting vessel:', err);
      alert('Error deleting vessel: ' + (err.response?.data?.message || 'Please try again.'));
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const resetForm    = () => { setFormData(emptyForm); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  // ── No session ─────────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="master-container">

      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>Vessel Master</h2>
          <p className="subtitle">Manage vessels and their carriers</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} /> Add Vessel
        </button>
      </div>

      {/* Toolbar */}
      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search vessels or carriers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <span>Active Only</span>
          </label>
        </div>

        <div className="toolbar-info">
          Total: {filteredVessels.length} {filteredVessels.length === 1 ? 'vessel' : 'vessels'}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading vessels...</div>
      ) : filteredVessels.length === 0 ? (
        <div className="empty-state">
          <p>No vessels found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Vessel Name</th>
                <th>Carrier</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVessels.map((vessel) => (
                <tr key={vessel.VesselID}>
                  <td className="font-mono">{vessel.VesselID}</td>
                  <td className="font-semibold">{vessel.Vessel}</td>
                  <td>
                    {vessel.CarrierName
                      ? <span>{vessel.CarrierName}</span>
                      : <span style={{ color: '#9ca3af' }}>—</span>
                    }
                  </td>
                  <td>
                    {Number(vessel.IsActive) === 1
                      ? <span className="status-badge active">Active</span>
                      : <span className="status-badge inactive">Inactive</span>
                    }
                  </td>
                  <td className="actions-column">
                    <button className="btn-icon btn-edit"   onClick={() => handleEdit(vessel)}                   title="Edit">   <Edit2  size={16} /></button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(vessel.VesselID)}        title="Delete"> <Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Vessel' : 'Add New Vessel'}</h3>
              <button className="btn-close" onClick={closeModal}><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4><Ship size={18} /> Vessel Information</h4>

                {/* Vessel Name */}
                <div className="form-group">
                  <label htmlFor="Vessel">
                    Vessel Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="Vessel"
                    value={formData.Vessel}
                    onChange={(e) => setFormData({ ...formData, Vessel: e.target.value })}
                    maxLength="255"
                    placeholder="e.g., MSC Gülsün, Ever Given"
                    className={errors.Vessel ? 'error' : ''}
                    autoFocus
                  />
                  {errors.Vessel && <span className="error-message">{errors.Vessel}</span>}
                </div>

                {/* Carrier Dropdown */}
                <div className="form-group">
                  <label htmlFor="CarrierID">Carrier</label>
                  <select
                    id="CarrierID"
                    value={formData.CarrierID || ''}
                    onChange={(e) => setFormData({ ...formData, CarrierID: e.target.value ? parseInt(e.target.value) : null })}
                  >
                    <option value="">— Select Carrier —</option>
                    {carriers.map((c) => (
                      <option key={c.CarrierID} value={c.CarrierID}>
                        {c.Carrier}
                      </option>
                    ))}
                  </select>
                  {carriers.length === 0 && (
                    <span style={{ fontSize: '12px', color: '#f59e0b' }}>
                      No active carriers found. Add carriers first.
                    </span>
                  )}
                </div>

                {/* Active checkbox */}
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', width: 'fit-content' }}>
                    <input
                      type="checkbox"
                      checked={!!formData.IsActive}
                      onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitLoading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vessel;